import * as React from 'react';
import {Query} from 'react-apollo';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {AutomatedEmailTable} from 'shells/automated-email-table/automated-email-table';

import type {EmailAutomationField} from 'nutshell-graphql-types';
import type {
    GetEmailAutomationsNeedingAttention as GetEmailAutomationsNeedingAttentionQuery,
    GetEmailAutomationsNeedingAttentionVariables,
    EmailAutomationStatusStatus as EmailAutomationStatus,
} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';

import {AutomationListWrapper} from './automation-list-wrapper';
import {convertEdgesToTableData} from './convert-edges-to-table-data';
import GET_NEEDS_ATTENTION from './graphql/queries/get-email-automations-needing-attention.graphql';

const LIMIT = 25;
const VALID_STATUSES: EmailAutomationStatus[] = ['NEEDS_ATTENTION'];
const STATUS = 'NEEDS_ATTENTION';

class NeedsAttentionQuery extends Query<
    GetEmailAutomationsNeedingAttentionQuery,
    GetEmailAutomationsNeedingAttentionVariables
> {}
type GraphqlProps = {
    onCancel: (id: string, currentEmailAutomationStatus?: any) => Promise<any>;
    onUpdateStatusField: (
        emailAutomationStatus: any,
        field: EmailAutomationField,
        newValue?: string | null | undefined
    ) => Promise<any>;
    hasAutomatedEmailSequences: boolean;
    hasConfiguredAutomations: boolean;
};

type Props = GraphqlProps & ContextRouter;

/**
 * This annoying layer just gets the render props from AutomationListWrapper so that
 * we can use them as `props` in the actual component to do things like set up hooks and effects.
 */
function AutomationsNeedsAttentionPlusCancelAndModified(props: ContextRouter) {
    return (
        <AutomationListWrapper
            query={GET_NEEDS_ATTENTION}
            fetchLimit={LIMIT}
            validStatuses={VALID_STATUSES}
            cacheKey='emailAutomationStatusesNeedsAttention'
        >
            {(wrapperProps) => <AutomationsNeedsAttentionComponent {...props} {...wrapperProps} />}
        </AutomationListWrapper>
    );
}

function AutomationsNeedsAttentionComponent(props: Props) {
    React.useEffect(() => {
        analyticsClient.track(analyticsClient.EVENTS.TRACK_AUTOMATIONS_NEEDS_ATTENTION_VIEWED);
    }, []);

    const {onCancel, onUpdateStatusField} = props;

    return (
        <NeedsAttentionQuery
            query={GET_NEEDS_ATTENTION}
            variables={{limit: LIMIT}}
            notifyOnNetworkStatusChange={true}
            // @ts-expect-error
            fetchPolicy='cache-and-network'
        >
            {({data, loading, fetchMore, error}) => {
                const {endCursor, hasNextPage} =
                    data && data.emailAutomationStatusesNeedsAttention
                        ? data.emailAutomationStatusesNeedsAttention.pageInfo
                        : {};

                const edges =
                    data && data.emailAutomationStatusesNeedsAttention
                        ? data.emailAutomationStatusesNeedsAttention.edges
                        : [];
                const tableData = convertEdgesToTableData(
                    edges.filter((edge) => VALID_STATUSES.includes(edge.node.status))
                );

                return (
                    <AutomatedEmailTable
                        // Can't use the data just yet
                        status={STATUS}
                        data={tableData}
                        isLoading={loading}
                        // @ts-expect-error
                        errMessage={safelyGetGraphQLErrorMessage(error)}
                        hasNextPage={hasNextPage}
                        history={props.history}
                        hasAutomatedEmailSequences={props.hasAutomatedEmailSequences}
                        hasConfiguredAutomations={props.hasConfiguredAutomations}
                        loadMore={() =>
                            fetchMore({
                                variables: {
                                    cursor: endCursor,
                                    limit: LIMIT,
                                },
                                updateQuery: (prev, {fetchMoreResult}) => {
                                    if (!fetchMoreResult) return prev;
                                    const prevData = prev.emailAutomationStatusesNeedsAttention;
                                    const newData =
                                        fetchMoreResult.emailAutomationStatusesNeedsAttention;

                                    return {
                                        ...prev,
                                        emailAutomationStatusesNeedsAttention: {
                                            ...prevData,
                                            pageInfo: newData.pageInfo,
                                            edges: [...prevData.edges, ...newData.edges],
                                        },
                                    };
                                },
                            })
                        }
                        onEmailAutomationStatusCancel={(emailAutomationStatusId) => {
                            const currentEmailAutomationStatus = edges.find(
                                (edge) => edge.node.id === emailAutomationStatusId
                            );

                            return onCancel(emailAutomationStatusId, currentEmailAutomationStatus);
                        }}
                        onUpdateField={(emailAutomationStatusId, fieldName, newValue) => {
                            const emailAutomationStatus = edges.find(
                                (edge) => edge.node.id === emailAutomationStatusId
                            );

                            return onUpdateStatusField(emailAutomationStatus, fieldName, newValue);
                        }}
                    />
                );
            }}
        </NeedsAttentionQuery>
    );
}

// @ts-expect-error
export const AutomationsNeedsAttention = withRouter<Record<any, any>>(
    AutomationsNeedsAttentionPlusCancelAndModified
);
